import React from 'react';
import { ResponsiveContainer, AreaChart as RechartsAreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import ChartHeader from './ChartHeader';
import { ChartHeaderProps } from './types';
import ChartCardWrapper from './ChartCardWrapper';

interface AreaChartData {
  [key: string]: string | number;
}

interface AreaChartProps extends ChartHeaderProps {
  data: AreaChartData[];
  dataKeys: string[];
  xAxisKey: string;
  colors?: string[];
  height?: number;
  graphHeight?: number;
  xAxisTitle?: string;
  yAxisTitle?: string;
}

const AreaChart: React.FC<AreaChartProps> = ({
  title,
  description,
  details,
  data,
  dataKeys,
  xAxisKey,
  colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'],
  height = 300,
  graphHeight = 300,
  xAxisTitle,
  yAxisTitle
}) => {
  return (
    <ChartCardWrapper sx={{ height }}>
      <ChartHeader title={title} description={description} details={details} />
      <ResponsiveContainer width="100%" height={graphHeight}>
        <RechartsAreaChart
          data={data}
          margin={{ top: 20, right: 30, left: 20, bottom: 15 }}
        >
          <defs>
            {dataKeys.map((key, index) => (
              <linearGradient key={`gradient-${key}`} id={`gradient-${key}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={colors[index % colors.length]} stopOpacity={0.3} />
                <stop offset="95%" stopColor={colors[index % colors.length]} stopOpacity={0.05} />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
          <XAxis
            dataKey={xAxisKey}
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: '#6B7280' }}
            label={xAxisTitle ? { value: xAxisTitle, position: 'insideBottom', offset: -5, style: { textAnchor: 'middle', fontSize: 12, fill: '#374151' } } : undefined}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: '#6B7280' }}
            label={yAxisTitle ? { value: yAxisTitle, angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 12, fill: '#374151' } } : undefined}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: '#F9FAFB',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              fontSize: '12px'
            }}
            cursor={{ stroke: 'rgba(59, 130, 246, 0.2)', strokeWidth: 1 }}
          />
          {dataKeys.map((key, index) => (
            <Area
              key={key}
              type="monotone"
              dataKey={key}
              stroke={colors[index % colors.length]}
              fill={`url(#gradient-${key})`}
              strokeWidth={2}
              dot={{ fill: colors[index % colors.length], strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, strokeWidth: 2, stroke: colors[index % colors.length] }}
            />
          ))}
        </RechartsAreaChart>
      </ResponsiveContainer>
    </ChartCardWrapper>
  );
};

export default AreaChart;
